import * as React from "react";
import CloseIcon from "@mui/icons-material/Close";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { Contact, DrawerState, Milestone, WhatsappGroup } from "./subscriptionTemplate.types";
import MilestoneSelect from "../CommunicationTemplete/MilestoneSelect";

interface Props {
  open: boolean;
  state: DrawerState;
  milestoneOptions: Milestone[];
  waContactOptions: Contact[];
  emailContactOptions: Contact[];
  groupOptions: WhatsappGroup[];
  onChange: (updater: (s: DrawerState) => DrawerState) => void;
  onClose: () => void;
  onSave: () => void;
}

type Errors = {
  templateName?: string;
  contacts?: string;
  waMilestones?: string;
  emailMilestones?: string;
};

const waContactLabel = (o: Contact) =>
  `${o.salutation || ""}. ${o.name || ""} (${o.countryCode || ""}${o.whatappNumber || ""}${o.organizationName ? `, ${o.organizationName}` : ""})`;

const emailContactLabel = (o: Contact) =>
  `${o.salutation || ""}. ${o.name || ""} (${o.email || ""}${o.organizationName ? `, ${o.organizationName}` : ""})`;

const chipSx = { "& .MuiAutocomplete-tag": { margin: "2px" } };

// Force the dropdown to always open below the field and never flip upward,
// even when the control grows taller after a selection.
const popperSx = {
  popper: {
    sx: { zIndex: 20000 },
    placement: "bottom-start" as const,
    modifiers: [
      { name: "flip", enabled: false },
      { name: "preventOverflow", enabled: false },
    ]
  },
};

export default function SubscriptionFormDrawer({
  open,
  state,
  milestoneOptions,
  waContactOptions,
  emailContactOptions,
  groupOptions,
  onChange,
  onClose,
  onSave,
}: Props) {
  const isEdit = !!state.id;
  const [errors, setErrors] = React.useState<Errors>({});

  React.useEffect(() => {
    if (open) setErrors({});
  }, [open]);

  const validate = (): boolean => {
    const next: Errors = {};
    if (!state.templateName.trim()) next.templateName = "Template name is required";
    if (!state.waContacts.length && !state.emailContacts.length)
      next.contacts = "Select at least one WhatsApp or Email recipient";
    if (state.waContacts.length && !state.waMilestones.length)
      next.waMilestones = "At least one WhatsApp milestone is required";
    if (state.emailContacts.length && !state.emailMilestones.length)
      next.emailMilestones = "At least one email milestone is required";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSaveClick = () => {
    if (!validate()) return;
    onSave();
  };

  const clearErr = (k: keyof Errors) =>
    setErrors((prev) => (prev[k] ? { ...prev, [k]: undefined } : prev));

  return (
    <>
      <div className={`ctpl-overlay${open ? " open" : ""}`} onClick={onClose} />
      <div className={`ctpl-drawer ctpl-drawer-wide${open ? " open" : ""}`}>
        <div className="ctpl-drawer-header">
          <h2>{isEdit ? `Edit — ${state.templateName}` : "Add Template"}</h2>
          <button className="ctpl-icon-btn" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </button>
        </div>

        <div className="ctpl-drawer-body">
          <div className="ctpl-field-group">
            <div className="ctpl-section-label">Template info</div>
            <label className="ctpl-field-label">
              Template name <span className="ctpl-required">*</span>
            </label>
            <input
              className={`ctpl-field-input${errors.templateName ? " error" : ""}`}
              placeholder="e.g. Booking Confirmed"
              value={state.templateName}
              onChange={(e) => {
                const v = e.target.value;
                onChange((s) => ({ ...s, templateName: v }));
                if (v.trim()) clearErr("templateName");
              }}
            />
            {errors.templateName && <div className="ctpl-field-error">{errors.templateName}</div>}
          </div>

          {errors.contacts && (
            <div className="ctpl-field-error" style={{ marginTop: -12, marginBottom: 12 }}>
              {errors.contacts}
            </div>
          )}

          {/* WhatsApp */}
          <div className="ctpl-field-group">
            <div className="ctpl-section-label">💬 WhatsApp channel</div>

            <label className="ctpl-field-label">Recipients</label>
            <Autocomplete
              multiple
              className="ctpl-autocomplete ctpl-chip-field"
              options={waContactOptions}
              getOptionLabel={waContactLabel}
              isOptionEqualToValue={(o, v) => o._id === v._id}
              value={state.waContacts}
              onChange={(_e, v) => {
                onChange((s) => ({ ...s, waContacts: v }));
                if (v.length) clearErr("contacts");
              }}
              disableCloseOnSelect
              fullWidth
              slotProps={popperSx}
              renderOption={(props, option, { selected }) => (
                <li {...props} key={option._id}>
                  <Checkbox icon={<CheckBoxOutlineBlankIcon fontSize="small" />} checkedIcon={<CheckBoxIcon fontSize="small" />} style={{ marginRight: 8 }} checked={selected} />
                  {waContactLabel(option)}
                </li>
              )}
              sx={chipSx}
              renderInput={(params) => <TextField {...params} variant="outlined" placeholder={state.waContacts.length ? "" : "+ Add recipient"} />}
            />

            <label className="ctpl-field-label" style={{ marginTop: 14 }}>Milestones</label>
            <div className={errors.waMilestones ? "ctpl-field-invalid" : ""}>
              <MilestoneSelect
                className="ctpl-autocomplete ctpl-chip-field"
                options={milestoneOptions}
                value={state.waMilestones}
                onChange={(v) => {
                  onChange((s) => ({ ...s, waMilestones: v }));
                  if (v.length) clearErr("waMilestones");
                }}
              />
            </div>
            {errors.waMilestones && <div className="ctpl-field-error">{errors.waMilestones}</div>}
          </div>

          {/* Email */}
          <div className="ctpl-field-group">
            <div className="ctpl-section-label">✉️ Email channel</div>

            <label className="ctpl-field-label">Recipients</label>
            <Autocomplete
              multiple
              className="ctpl-autocomplete ctpl-chip-field"
              options={emailContactOptions}
              getOptionLabel={emailContactLabel}
              isOptionEqualToValue={(o, v) => o._id === v._id}
              value={state.emailContacts}
              onChange={(_e, v) => {
                onChange((s) => ({ ...s, emailContacts: v }));
                if (v.length) clearErr("contacts");
              }}
              disableCloseOnSelect
              fullWidth
              slotProps={popperSx}
              renderOption={(props, option, { selected }) => (
                <li {...props} key={option._id}>
                  <Checkbox icon={<CheckBoxOutlineBlankIcon fontSize="small" />} checkedIcon={<CheckBoxIcon fontSize="small" />} style={{ marginRight: 8 }} checked={selected} />
                  {emailContactLabel(option)}
                </li>
              )}
              sx={chipSx}
              renderInput={(params) => <TextField {...params} variant="outlined" placeholder={state.emailContacts.length ? "" : "+ Add recipient"} />}
            />

            <label className="ctpl-field-label" style={{ marginTop: 14 }}>Milestones</label>
            <div className={errors.emailMilestones ? "ctpl-field-invalid" : ""}>
              <MilestoneSelect
                className="ctpl-autocomplete ctpl-chip-field"
                options={milestoneOptions}
                value={state.emailMilestones}
                onChange={(v) => {
                  onChange((s) => ({ ...s, emailMilestones: v }));
                  if (v.length) clearErr("emailMilestones");
                }}
              />
            </div>
            {errors.emailMilestones && <div className="ctpl-field-error">{errors.emailMilestones}</div>}
          </div>

          {/* Groups */}
          <div className="ctpl-field-group">
            <div className="ctpl-section-label">👥 WhatsApp groups <span className="ctpl-optional">(optional)</span></div>

            <label className="ctpl-field-label">Groups</label>
            <Autocomplete
              multiple
              className="ctpl-autocomplete ctpl-chip-field"
              options={groupOptions}
              getOptionLabel={(o: any) => o.name || ""}
              isOptionEqualToValue={(o, v) => o._id === v._id}
              value={state.whatsappGroups}
              onChange={(_e, v) => onChange((s) => ({ ...s, whatsappGroups: v }))}
              disableCloseOnSelect
              fullWidth
              slotProps={popperSx}
              sx={chipSx}
              renderInput={(params) => <TextField {...params} variant="outlined" placeholder={state.whatsappGroups.length ? "" : "+ Add group"} />}
            />

            <label className="ctpl-field-label" style={{ marginTop: 14 }}>Group milestones</label>
            <MilestoneSelect
              className="ctpl-autocomplete ctpl-chip-field"
              options={milestoneOptions}
              value={state.whatsappGroupMilestones}
              onChange={(v) => onChange((s) => ({ ...s, whatsappGroupMilestones: v }))}
            />
          </div>

          {/* <div className="ctpl-toggle-row">
            <div>
              <div style={{ fontSize: 13.5, fontWeight: 600 }}>Active</div>
              <div className="ctpl-hint">Inactive templates won't be used to send alerts.</div>
            </div>
            <button
              type="button"
              className={`ctpl-switch${state.isActive ? " on" : ""}`}
              onClick={() => onChange((s) => ({ ...s, isActive: !s.isActive }))}
            />
          </div> */}
        </div>

        <div className="ctpl-drawer-footer">
          <button className="ctpl-btn ctpl-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="ctpl-btn ctpl-btn-primary" onClick={handleSaveClick}>
            {isEdit ? "Save changes" : "Save template"}
          </button>
        </div>
      </div>
    </>
  );
}